export const DOMAIN_VERSION = 1 as const;

export type Brand<Value, Name extends string> = Value & { readonly __brand: Name };
export type UUID = Brand<string, 'UUID'>;
export type Instant = Brand<string, 'Instant'>;
export type LocalDate = Brand<string, 'LocalDate'>;

export type EntityMetadata = {
  id: UUID;
  createdAt: Instant;
  updatedAt: Instant;
  revision: number;
  deletedAt?: Instant;
};

export type RegistrationWindow = {
  startsAt: Instant;
  endsAt: Instant;
  label: string;
};

export type Exam = EntityMetadata & {
  title: string;
  presetId?: string;
  examDate?: LocalDate | null | undefined;
  timezone: string;
  registrationWindows?: RegistrationWindow[];
  result?: 'passed' | 'failed' | 'unknown';
  attempts?: Array<{ examDate: LocalDate; result: 'passed' | 'failed' | 'unknown' }>;
  stage?: string;
  retainedArchive?: boolean;
};

export type Subject = EntityMetadata & {
  examId: UUID;
  name: string;
  /** null until the learner knows the real size of this subject. */
  totalUnits: number | null;
  unitLabel: 'page' | 'lecture' | 'question' | 'custom';
  customUnitLabel?: string;
  sortOrder: number;
};

export type StudyLog = EntityMetadata & {
  examId: UUID;
  subjectId: UUID;
  studyDate: LocalDate;
  startedAt?: Instant;
  endedAt?: Instant;
  minutes?: number;
  round: number;
  unitStart: number;
  unitEnd: number;
  memo?: string;
  source: 'manual' | 'room_prefill' | 'onboarding_import' | 'plan';
  planItemId?: UUID;
};

export type WrongNote = EntityMetadata & {
  examId: UUID;
  subjectId: UUID;
  photoAssetId?: UUID;
  text?: string;
  memo?: string;
  tags: string[];
  reviewCount: number;
  lastRecall?: 'remembered' | 'uncertain' | 'forgotten';
  lastReviewedAt?: Instant;
  relearningAt?: Instant;
  masteredAt?: Instant;
};

export type PhotoAsset = {
  id: UUID;
  original: Blob;
  thumbnail: Blob;
  mimeType: string;
  width: number;
  height: number;
  byteLength: number;
  sha256: string;
  createdAt: Instant;
  deletedAt?: Instant;
};

export type PlanParams = {
  timezone: string;
  targetRounds: number;
  studyWeekdays: number[];
  excludedDates: LocalDate[];
  maxDailyUnits?: number;
  subjectWeights: Record<string, number>;
};

export type Plan = {
  id: UUID;
  examId: UUID;
  version: number;
  status: 'draft' | 'active' | 'superseded';
  params: PlanParams;
  generatedAt: Instant;
  confirmedAt?: Instant;
  supersedesId?: UUID;
};

export type PlanItem = {
  id: UUID;
  planId: UUID;
  date: LocalDate;
  subjectId: UUID;
  round: number;
  unitStart: number;
  unitEnd: number;
  status: 'planned' | 'done' | 'skipped';
};

export type Milestone = {
  id: UUID;
  key: string;
  examId?: UUID;
  earnedAt: Instant;
  sourceEntityId?: UUID;
};

export type MogakSession = {
  id: UUID;
  roomId: UUID;
  examId: UUID;
  joinedAt: Instant;
  leftAt?: Instant;
  activeMinutes: number;
  outcome: 'prefill_saved' | 'discarded' | 'disconnected' | 'kicked';
  savedLogId?: UUID;
};

export type MockScore = EntityMetadata & {
  examId: UUID;
  title: string;
  studyDate: LocalDate;
  scores: Array<{ subjectId: UUID; score: number }>;
};
export type NoteInbox = EntityMetadata & { photoAssetId: UUID };
export type PortableSetting = { key: string; value: unknown; updatedAt: Instant };
export function isPortableSetting(key: string): boolean {
  return /^(weekly-goal|exam-checklist|pass-criteria):[a-zA-Z0-9-]+$/.test(key);
}
export type StudyStoreMap = {
  mockScores: MockScore;
  noteInbox: NoteInbox;
  exams: Exam;
  subjects: Subject;
  studyLogs: StudyLog;
  wrongNotes: WrongNote;
  photoAssets: PhotoAsset;
  plans: Plan;
  planItems: PlanItem;
  milestones: Milestone;
  mogakSessions: MogakSession;
};

export type StudyStoreName = keyof StudyStoreMap;

export * from './local-date';
export * from './milestones';
export * from './plan';
export * from './progress';
export * from './reminders';
export * from './rooms';
export * from './today-session';
export * from './wrong-note-review';

export * from './app-routes';
export * from './exam-catalog';
export * from './scores';
export * from './study-report';
export { default as GUIDE_CONTENT } from './guides.json';
